import { isReducedMotion } from '@atlaskit/motion';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { Percentage, Pixels } from '../types';

const OFFSET: number = gridSize * -4;
const TIMELINE_ID = 'sr-timeline' as const;

type ScrollAmount<Units> = {
	left?: Units;
	top?: Units;
};

type ScrollBehavior = 'auto' | 'smooth';

type ScrollToPercentagesInput = {
	left?: Percentage;
	top?: Percentage;
	offset?: Pixels;
	behavior?: ScrollBehavior;
};

/* WARNING: THIS IS AN ESCAPE HATCH. USE SPARINGLY.
 * If components from outside the table need to manipulate the timeline, use this.
 * E.g. a today button in the header that scrolls the user to todays' date.
 */

const scrollToPercentages = ({
	left,
	top,
	offset = OFFSET,
	behavior = 'auto',
}: ScrollToPercentagesInput): void => {
	const timelineElem: HTMLElement | null = document.getElementById(TIMELINE_ID);
	if (timelineElem !== null) {
		const { scrollWidth } = timelineElem;
		timelineElem.scrollTo({
			left: left !== undefined ? (left / 100) * scrollWidth + offset : undefined,
			top: top !== undefined ? (top / 100) * scrollWidth + offset : undefined,
			behavior: isReducedMotion() ? 'auto' : behavior,
		});
	}
};

// NOTE - we don't use Elem.scrollBy as it breaks IE Edge 18
const scrollByPixels = ({ left = 0, top = 0 }: ScrollAmount<Pixels>): void => {
	const timelineElem: HTMLElement | null = document.getElementById(TIMELINE_ID);

	if (timelineElem !== null) {
		timelineElem.scrollLeft += left;
		timelineElem.scrollTop += top;
	}
};
type ScrollState = {
	clientWidth: Pixels;
	scrollWidth: Pixels;
	scrollLeft: Pixels;
};

const getScrollState = (): ScrollState => {
	const timelineElem: HTMLElement | null = document.getElementById(TIMELINE_ID);

	if (timelineElem === null) {
		return { clientWidth: 0, scrollWidth: 0, scrollLeft: 0 };
	}

	const { clientWidth, scrollWidth, scrollLeft } = timelineElem;
	return { clientWidth, scrollWidth, scrollLeft };
};

const timelineScroller = {
	getScrollState,
	scrollByPixels,
	scrollToPercentages,
} as const;

export { timelineScroller, TIMELINE_ID, OFFSET };
export type { ScrollAmount, ScrollState };
